'use client';

import { useCurrencyContext } from '@contexts/currency-context';
import { MAP_SECTION_COLOR } from '@config/index';
import { LeagueEvent } from '@interfaces/APITravel';
import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { Button, Eye, EyeOff } from '@v2/ui';
import { cn } from '@v2/utils';
import { useBuildPackagesStore } from '@store/build-package-store';
import { TicketMap } from '@ticketevolution/seatmaps-client';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTicketsStore } from 'src/stores/tickets-store';
import { normalizeSectionId } from '../utils/section-ids';
import { useMapTicketGroups } from '../utils/use-filtered-tickets';
import { useTicketPriceOffset } from '../utils/use-ticket-price-offset';
import MissingSeatMapLogo from './MissingSeatMapLogo';
import {
  type ArenaPriceMarker,
  useArenaPriceMarkers,
} from './use-arena-price-markers';
import useTicketEvoMap from './use-ticket-evo-map';

type Props = {
  event: LeagueEvent;
  ticketGroups: TicketGroup[];
};

export const ArenaView = memo(function ArenaView({
  event,
  ticketGroups,
}: Props) {
  const { venue, configuration } = event;
  const venueId = venue?.id != null ? String(venue.id) : '';
  const configurationId =
    configuration?.id != null ? String(configuration.id) : '';

  const { ref: mapRef, handle } = useTicketEvoMap();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const selectedSections = useTicketsStore((state) => state.selectedGroups);
  const setSelectedGroups = useTicketsStore((state) => state.setSelectedGroups);
  const ticketSelected = useTicketsStore((state) => state.ticketSelected);
  const hideMapPrices = useTicketsStore((state) => state.hideMapPrices);
  const toggleBoolean = useTicketsStore((state) => state.toggleBoolean);
  const { priceRate, currency } = useCurrencyContext();

  const highlightSection = ticketSelected?.tevo_section_name
    ? normalizeSectionId(ticketSelected.tevo_section_name)
    : undefined;
  const mapIdentity = `${venueId}:${configurationId}`;

  const filteredGroups = useMapTicketGroups(ticketGroups);
  const mapTicketGroups = useMemo(
    () =>
      filteredGroups.map((group) => ({
        ...group,
        tevo_section_name: group.tevo_section_name.trim(),
      })),
    [filteredGroups]
  );
  const priceOffset = useTicketPriceOffset(ticketGroups);
  const isBundle = useBuildPackagesStore((state) => state.isBundle);

  const selectedFillCss = useMemo(() => {
    if (!highlightSection) return '';
    const escapedSection = CSS.escape(highlightSection);

    return `[data-arena-map] [data-section-id="${escapedSection}"] path,
      [data-arena-map] [data-section-id="${escapedSection}"] {
        fill: #FA2846 !important;
        stroke: #9f1239 !important;
        stroke-width: 2px !important;
      }`;
  }, [highlightSection]);

  const nativeTooltipCss =
    '[data-arena-map] [data-testid="seatmaps-tooltip"] { display: none !important; }';

  useEffect(() => {
    handle?.updateTicketGroups(mapTicketGroups);
  }, [handle, mapTicketGroups]);

  useEffect(() => {
    if (!selectedSections.length) handle?.deselectSection();
  }, [handle, selectedSections]);

  // Visually emphasize the chosen ticket's section without filtering the list
  // (selectSection would fire onSelection and narrow Sidebar results).
  useEffect(() => {
    if (!handle) return;

    try {
      if (highlightSection) {
        // A card can refer to a tile outside the currently zoomed viewport.
        mapRef.current?.zoom?.reset();
        handle.highlightSection(highlightSection);
        return () => handle.unhighlightSection(highlightSection);
      }
    } catch {
      // Section may not exist on the current map configuration
    }
  }, [handle, highlightSection, ticketSelected?.id, mapRef]);

  const priceMarkers = useArenaPriceMarkers({
    mapRef,
    containerRef: mapContainerRef,
    ticketGroups: mapTicketGroups,
    selectedSections,
    focusedSection: highlightSection,
    mapIdentity,
    enabled: Boolean(
      handle &&
      ticketGroups.length &&
      venueId &&
      configurationId &&
      !hideMapPrices
    ),
  });

  if (!ticketGroups?.length) return null;
  if (!venueId || !configurationId) return null;

  return (
    <div
      data-arena-map
      ref={mapContainerRef}
      className="relative h-full overflow-hidden lg:h-[720px]"
    >
      <style>{`${selectedFillCss}${nativeTooltipCss}`}</style>
      <TicketMap
        ref={mapRef}
        venueId={venueId}
        configurationId={configurationId}
        mapsDomain="/api/te/map-assets"
        ticketGroups={mapTicketGroups}
        sectionPercentiles={{
          0.2: MAP_SECTION_COLOR,
          0.4: MAP_SECTION_COLOR,
          0.6: MAP_SECTION_COLOR,
          0.8: MAP_SECTION_COLOR,
          1: MAP_SECTION_COLOR,
        }}
        showLegend={false}
        showControls
        selectedSections={selectedSections}
        onSelection={setSelectedGroups}
        showZoomHelper={false}
        missingSeatMapLogo={<MissingSeatMapLogo />}
      />
      <Button
        type="button"
        variant="secondary"
        size="xs"
        aria-pressed={hideMapPrices}
        data-testid="toggle-map-prices"
        onClick={() => toggleBoolean('hideMapPrices')}
        className="absolute right-2.5 top-2.5 z-10 min-h-11 gap-1.5 bg-white/95 lg:min-h-0"
      >
        {hideMapPrices ? (
          <Eye aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        {hideMapPrices ? 'Show prices' : 'Hide prices'}
      </Button>
      {ticketSelected && (
        <div
          role="status"
          className="pointer-events-none absolute bottom-2 left-1/2 z-10 max-w-[calc(100%-16px)] -translate-x-1/2 truncate rounded-lg bg-white/95 px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm"
        >
          Selected: Section {ticketSelected.section}, Row {ticketSelected.row}
        </div>
      )}
      <ArenaPriceMarkers
        markers={priceMarkers}
        priceOffset={priceOffset}
        isBundle={isBundle}
        priceRate={priceRate}
        currency={currency}
        selectedSections={selectedSections}
        onToggleSection={(sectionId) => {
          if (selectedSections.includes(sectionId))
            handle?.deselectSection(sectionId);
          else handle?.selectSection(sectionId);
        }}
      />
    </div>
  );
});

type ArenaPriceMarkersProps = {
  markers: ArenaPriceMarker[];
  priceOffset: number;
  isBundle: boolean;
  priceRate: number;
  currency: string;
  selectedSections: string[];
  onToggleSection: (sectionId: string) => void;
};

function ArenaPriceMarkers({
  markers,
  priceOffset,
  isBundle,
  priceRate,
  currency,
  selectedSections,
  onToggleSection,
}: ArenaPriceMarkersProps) {
  if (!markers.length) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-0">
      {markers.map((marker) => {
        const formattedPrice = formatCurrencyV2(
          Math.max(0, marker.price - priceOffset),
          priceRate,
          currency,
          false,
          false,
          true,
          true
        );

        return (
          <button
            type="button"
            key={marker.sectionId}
            data-section-id={marker.sectionId}
            data-testid={`arena-price-marker-${marker.sectionId}`}
            aria-label={`Section ${marker.sectionName}, from ${formattedPrice}${isBundle ? ' upgrade per person' : ' per person'}`}
            aria-pressed={selectedSections.includes(marker.sectionId)}
            title={`Section ${marker.sectionName}`}
            tabIndex={marker.isVisible ? 0 : -1}
            aria-hidden={!marker.isVisible}
            disabled={!marker.isVisible}
            onClick={() => onToggleSection(marker.sectionId)}
            className={cn(
              'absolute min-h-11 min-w-[100px] touch-manipulation whitespace-nowrap rounded-xl border px-2 py-1 text-center shadow-[0_4px_12px_rgba(15,23,42,0.16)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 lg:min-w-[88px] lg:px-2.5 lg:py-1.5 lg:shadow-[0_6px_18px_rgba(15,23,42,0.18)]',
              marker.isSelected
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-white/90 bg-white/95 text-gray-900',
              marker.isVisible
                ? 'pointer-events-auto opacity-100'
                : 'invisible opacity-0'
            )}
            style={{
              left: marker.left,
              top: marker.top,
              transform: 'translate(-50%, calc(-100% - 8px))',
            }}
          >
            <div className="flex flex-col items-center gap-0.5 leading-none">
              <span className="text-[12px] font-extrabold tracking-[-0.02em] lg:text-base">
                From {isBundle ? '+ ' : ''}
                {formattedPrice}
              </span>
            </div>
            <span
              aria-hidden="true"
              className={cn(
                'absolute -bottom-1.5 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-t-[7px] border-solid border-x-transparent lg:-bottom-2 lg:border-x-[7px] lg:border-t-[8px]',
                marker.isSelected ? 'border-t-primary-500' : 'border-t-white'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
